import apiClient from './apiClient';
import { analyticsService } from './analyticsService';

export interface ConversationMessage {
  id: number;
  type: string;
  timestamp: string;
  content?: string;
  // Type-specific fields stored in the message itself
  [key: string]: any;
}

export interface ConversationResponse {
  conversation_id: string;
  user_id: string;
  title?: string;
  created_at: string;
  updated_at: string;
  status: string;
  messages: ConversationMessage[];
}

export interface ConversationSummary {
  conversation_id: string;
  title: string;
  created_at: string;
  updated_at: string;
  status: string;
  message_count: number;
  last_message_preview: string;
}

export interface ConversationListResponse {
  conversations: ConversationSummary[];
  total_count: number;
  limit: number;
  offset: number;
}

const conversationService = {
  /**
   * Get a specific conversation with all messages
   */
  async getConversation(conversationId: string, userId: string): Promise<ConversationResponse> {
    const response = await apiClient.get<ConversationResponse>(
      `/chat-conversations/${conversationId}`,
      {
        params: { user_id: userId }
      }
    );

    // Convert timestamp strings back to Date objects for frontend use
    return {
      ...response.data,
      messages: response.data.messages.map((msg: ConversationMessage) => ({
        ...msg,
        timestamp: msg.timestamp // Keep as string initially, will convert in store
      }))
    };
  },

  /**
   * List user's conversations
   */
  async listConversations(
    userId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ConversationListResponse> {
    const response = await apiClient.get<ConversationListResponse>(
      '/chat-conversations',
      {
        params: {
          user_id: userId,
          limit,
          offset
        }
      }
    );

    return response.data;
  },

  /**
   * Append messages to an existing conversation
   */
  async updateConversation(
    conversationId: string,
    userId: string,
    messages: ConversationMessage[]
  ): Promise<void> {
    await apiClient.patch(
      `/chat-conversations/${conversationId}`,
      { messages },
      { params: { user_id: userId } }
    );
  },

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: string, userId: string): Promise<void> {
    await apiClient.delete(`/chat-conversations/${conversationId}`, {
      params: { user_id: userId }
    });

    // Track conversation deletion
    analyticsService.trackConversationDeleted(conversationId);
  }
};

export default conversationService;
